import {
  confirmPolicy,
  openPolicy,
  openPolicyFailure,
  openPolicySuccess,
  request,
  requestFailure,
  requestSuccess
} from './request-maintenance.actions';
import {catchError, filter, map, of, switchMap} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {inject, Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {GlobalState} from '../../../../../store/store.model';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {RequestMaintenanceApiService, RequestSupportResponse} from '../../../services/request-maintenance-api.service';
import {CommonService, DisclosureType} from '../../../../../shared/services/common.service';
import {MatDialog} from '@angular/material/dialog';
import {
  RequestMaintenancePolicyDialogComponent
} from '../../../../../shared/components/request-maintenance-policy-dialog/request-maintenance-policy-dialog.component';
import {
  RequestMaintenancePolicyErrorDialogComponent
} from '../../../../../shared/components/request-maintenance-policy-error-dialog/request-maintenance-policy-error-dialog.component';
import {RequestMaintenanceService} from '../../../services/request-maintenance.service';

@Injectable()
export class RequestMaintenanceEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _requestMaintenanceApiService = inject(RequestMaintenanceApiService);
  private readonly _requestMaintenanceService = inject(RequestMaintenanceService);
  private readonly _commonService = inject(CommonService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _transloco = inject(TranslocoService);
  private readonly _dialog = inject(MatDialog);

  policy$ = createEffect(() =>
    this._actions$.pipe(
      ofType(openPolicy),
      switchMap(({locationId}) =>
        this._commonService.getPolicyList(DisclosureType.ADDRESS, 'my_visionnaire').pipe(
          map((resp: any) => Array.isArray(resp?.result.data) ? resp.result.data[0] : resp),
          filter((item: any) => !!item?.content),
          map((item: any) =>
            openPolicySuccess({
              policy: {
                id: String(item.id ?? item.policyId),
                name: String(item.name ?? ''),
                content: item.content ?? ''
              },
              locationId
            })
          ),
          catchError((error) => of(openPolicyFailure()))
        )
      )
    )
  );

  askConsent$ = createEffect(() =>
    this._actions$.pipe(
      ofType(openPolicySuccess),
      switchMap(({policy, locationId}) => {
        const ref = this._dialog.open(RequestMaintenancePolicyDialogComponent, {
          data: {
            name: policy.name,
            content: policy.content
          }
        });
        return ref.afterClosed().pipe(
          map((accepted: boolean) => confirmPolicy({accepted, locationId, policyId: policy.id}))
        );
      })
    )
  );

  openPolicyFailure$ = createEffect(() =>
    this._actions$.pipe(
      ofType(openPolicyFailure),
      switchMap(() => {
        const ref = this._dialog.open(RequestMaintenancePolicyErrorDialogComponent);
        return ref.afterClosed()
      })
    )
  );

  // 3) Se accettato, invia richiesta
  requestOnConfirm$ = createEffect(() =>
    this._actions$.pipe(
      ofType(confirmPolicy),
      filter(a => a.accepted),
      map(a => request({locationId: a.locationId, policyId: a.policyId}))
    )
  );

  request$ = createEffect(() =>
    this._actions$.pipe(
      ofType(request),
      switchMap(({locationId, policyId}) =>
        this._requestMaintenanceApiService.requestSupport({locationId, policyId}).pipe(
          map((resp: RequestSupportResponse) => {
            const status = resp?.result?.status ?? 'error';

            if (status === 'success') {
              return requestSuccess();
            }

            const message = resp?.result?.message;
            return requestFailure({
              errorKey: this._requestMaintenanceService.mapBackendErrorToKey(message),
              rawMessage: message ? message : ''
            });
          }),
          // Errore di rete / HTTP != 2xx
          catchError((err) =>
            of(
              requestFailure({
                errorKey: 'profile.request_maintenance.request_network_error',
                rawMessage: err?.message ?? null
              })
            )
          )
        )
      )
    )
  );


  toastOnSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(requestSuccess),
        map(() => {
          const msg = this._transloco.translate('profile.request_maintenance.request_success') || 'Richiesta inviata';
          this._snackBar.open(msg, 'Close', {panelClass: 'snackbar'});
        })
      ),
    {dispatch: false}
  );

  toastOnFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(requestFailure),
        map(({rawMessage}) => {
          if (rawMessage) {
            this._snackBar.open(rawMessage, 'Close', {panelClass: 'snackbar'});
          }

        })
      ),
    {dispatch: false}
  );
}
